import { useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency as formatMoney, formatDate as formatSharedDate } from "@/lib/i18n";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { BarChart3, CalendarClock, Mail, PlayCircle } from "lucide-react";

/**
 * D5: Proof-of-Value Report
 * Monthly report per client: leads, pipeline, revenue, automations, reviews.
 * Agency-branded (logo + colors from white-label config).
 * Download as PDF. Schedule monthly auto-send.
 */

interface ReportData {
  tenantId: string;
  tenantName: string;
  month: string;
  period: string;
  generatedAt: string | null;
  metrics: {
    newLeads: number;
    pipelineValueCents: number;
    revenueCents: number;
    automationsRun: number;
    emailsSent: number;
    reviewsCollected: number;
    bookingsCreated: number;
  };
  branding: {
    logoUrl?: string;
    primaryColor?: string;
    agencyName?: string;
  };
  reportConfig?: {
    reportType?: string;
    recipients?: string[];
    scheduleCron?: string | null;
    nextRunAt?: string | null;
    lastRunAt?: string | null;
    latestRun?: {
      id: string;
      month: string;
      status: string;
      generatedAt: string | null;
      sentAt: string | null;
    } | null;
  };
}

export default function ProofOfValuePage() {
  const [tenantId, setTenantId] = useState("");
  const [report, setReport] = useState<ReportData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!tenantId) return;
    setIsGenerating(true);
    setError(null);
    try {
      const data = await apiFetch<{ data: ReportData }>(
        `/platform/agency/proof-of-value/${tenantId}`,
      );
      setReport(data.data);
    } catch {
      setReport(null);
      setError("The mounted agency reporting seam is currently unavailable for this tenant.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPdf = () => {
    // In production: call server-side PDF generation endpoint
    // For now: print the report div
    window.print();
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">Proof-of-Value Report</h1>

      {/* Generator */}
      <div className="mb-6 flex gap-3">
        <input
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          placeholder="Enter tenant ID..."
          className="flex-1 rounded-md border border-input px-3 py-2 text-sm"
        />
        <button
          onClick={handleGenerate}
          disabled={isGenerating || !tenantId}
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          {isGenerating ? "Generating..." : "Generate Report"}
        </button>
      </div>

      {error ? (
        <div className="mb-6 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {isGenerating && !report ? (
        <div className="space-y-4">
          <Skeleton className="h-8 w-56" />
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <SkeletonCard key={i} className="h-28" />
            ))}
          </div>
        </div>
      ) : null}

      {!report && !isGenerating ? (
        <EmptyState
          icon={<BarChart3 className="h-8 w-8" />}
          title="Generate a mounted report"
          description="Enter a tenant ID to load the real `/platform/agency/proof-of-value/:tenantId` reporting contract with report-run and delivery metadata."
          variant="info"
        />
      ) : null}

      {report && (
        <div className="print:m-0 print:shadow-none">
          <div className="mb-4 flex justify-end gap-2 print:hidden">
            <button onClick={handleDownloadPdf} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent">
              Download PDF
            </button>
          </div>

          <div id="report" className="rounded-lg border border-border bg-background p-8 shadow-sm">
            {/* Header with agency branding */}
            <div className="mb-8 flex items-center justify-between border-b pb-4" style={{ borderColor: report.branding.primaryColor ?? "#e5e7eb" }}>
              <div className="flex items-center gap-3">
                {report.branding.logoUrl && <img src={report.branding.logoUrl} alt="" className="h-10 w-10 rounded" />}
                <div>
                  <h2 className="text-xl font-bold" style={{ color: report.branding.primaryColor ?? "#111" }}>
                    {report.branding.agencyName ?? "Agency"} — Monthly Report
                  </h2>
                  <p className="text-sm text-[var(--color-muted-foreground)]">Prepared for: {report.tenantName}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium">{report.period}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Generated {formatReportDate(report.generatedAt)}
                </p>
              </div>
            </div>

            <div className="mb-6 grid gap-4 md:grid-cols-3">
              <MetadataCard
                icon={<CalendarClock className="h-4 w-4" />}
                label="Schedule"
                value={report.reportConfig?.scheduleCron ?? "Manual generation"}
                helper={report.reportConfig?.nextRunAt ? `Next run ${formatReportDate(report.reportConfig.nextRunAt)}` : "No scheduled delivery mounted"}
              />
              <MetadataCard
                icon={<Mail className="h-4 w-4" />}
                label="Recipients"
                value={report.reportConfig?.recipients?.length ? report.reportConfig.recipients.join(", ") : "No recipients configured"}
                helper={report.reportConfig?.reportType ? `Report type: ${report.reportConfig.reportType}` : "Tenant-scoped delivery metadata"}
              />
              <MetadataCard
                icon={<PlayCircle className="h-4 w-4" />}
                label="Latest run"
                value={formatRunStatus(report.reportConfig?.latestRun?.status)}
                helper={report.reportConfig?.latestRun?.generatedAt ? `Generated ${formatReportDate(report.reportConfig.latestRun.generatedAt)}` : "No prior run recorded"}
              />
            </div>

            {/* Metrics grid */}
            <div className="grid gap-4 md:grid-cols-3">
              <MetricCard label="New Leads" value={report.metrics.newLeads.toString()} color={report.branding.primaryColor} />
              <MetricCard label="Pipeline Value" value={formatMoney(report.metrics.pipelineValueCents)} color={report.branding.primaryColor} />
              <MetricCard label="Revenue" value={formatMoney(report.metrics.revenueCents)} color={report.branding.primaryColor} />
              <MetricCard label="Automations Run" value={report.metrics.automationsRun.toString()} color={report.branding.primaryColor} />
              <MetricCard label="Emails Sent" value={report.metrics.emailsSent.toString()} color={report.branding.primaryColor} />
              <MetricCard label="Reviews Collected" value={report.metrics.reviewsCollected.toString()} color={report.branding.primaryColor} />
            </div>

            {/* Footer */}
            <div className="mt-8 border-t pt-4 text-center text-xs text-[var(--color-muted-foreground)]">
              <p>This report was generated from the mounted Stero agency reporting seam.</p>
              <p>For questions, contact your agency representative.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4">
      <p className="text-xs text-[var(--color-muted-foreground)]">{label}</p>
      <p className="text-2xl font-bold" style={{ color: color ?? "#111" }}>{value}</p>
    </div>
  );
}

function MetadataCard({
  icon,
  label,
  value,
  helper,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  helper: string;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-sm font-medium text-[var(--color-foreground)]">{value}</p>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{helper}</p>
    </div>
  );
}

function formatReportDate(value: string | null | undefined): string {
  if (!value) {
    return "Unavailable";
  }

  return formatSharedDate(value, "en-US");
}

function formatRunStatus(status: string | null | undefined): string {
  if (!status) {
    return "No run history";
  }

  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
